<script lang="ts">
	// A6: which restaurants to contact about renewing (UC-24). Every one whose
	// subscription ends within 14 days, or has ended, earliest end first.
	// Every date here is Bosla's own setting, not anything the restaurant did,
	// and the order follows it, never sales.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { api } from '../api/client';
	import type { components } from '../api/types';
	import { copy } from './copy';
	import { day } from './format';
	import { lastChangedAccount, openAccount } from './nav.svelte';
	import { sessionEnded } from './session.svelte';

	type Subscription = components['schemas']['AdminSubscriptionRowBody'];

	let rows = $state<Subscription[] | null>(null);
	let failed = $state(false);

	async function load() {
		failed = false;
		try {
			const { data, response } = await api.GET('/api/admin/subscriptions');
			if (response.status === 401) {
				sessionEnded();
				return;
			}
			if (!data) {
				failed = true;
				return;
			}
			rows = data.rows ?? [];
		} catch {
			failed = true;
		}
	}

	// Loads once, and again when an account opened from here changed, so Back
	// shows the list as it now is: a new end date can move a restaurant off it.
	$effect(() => {
		void lastChangedAccount();
		void load();
	});

	const columns = [
		{ label: copy.restaurantOwnerColumn, w: 'minmax(0,2fr)' },
		{ label: copy.endsColumn, w: '80px' },
		{ label: '', w: 'minmax(0,1fr)' },
		{ label: copy.stateColumn, w: '110px' },
		{ label: '', w: '64px' },
	];
</script>

<div class="subscriptions" data-testid="admin-subscriptions">
	{#if failed}
		<Band
			tone="flag"
			action={copy.retry}
			onAction={() => void load()}
			testId="admin-subscriptions-failed"
		>
			{copy.subscriptionsFailed}
		</Band>
	{:else if rows === null}
		<div class="wait">
			<Loading label={copy.subscriptionsLoading} caption={copy.subscriptionsLoading} size="lg" />
		</div>
	{:else if rows.length === 0}
		<Card pad testId="admin-subscriptions-none">
			<p class="empty">{copy.noSubscriptions}</p>
		</Card>
	{:else}
		<TablePanel
			{columns}
			title={copy.toContact}
			note={copy.subscriptionsNote}
			testId="admin-subscriptions-list"
		>
			{#each rows as r (r.accountId)}
				<Row testId="admin-subscription-{r.accountId}">
					<span class="who">
						<span class="restaurant">{r.restaurantName}</span>
						<span class="owner">{r.ownerName} · {r.ownerEmail}</span>
					</span>
					<span class="mono small">{day(r.endsOn)}</span>
					<span class="small strong">{copy.leftLine(r.daysLeft)}</span>
					<span class="state">
						<Chip
							text={r.state === 'suspended' ? copy.actionSuspended : copy.active}
							tone={r.state === 'suspended' ? 'flag' : 'ok'}
							size="md"
							dot
						/>
					</span>
					<span class="open">
						<Button
							testId="admin-subscription-open-{r.accountId}"
							label={copy.open}
							tone="text"
							size="sm"
							onPress={() => openAccount(r.accountId, '/subscriptions')}
						/>
					</span>
				</Row>
			{/each}
		</TablePanel>
		<p class="note">{copy.subscriptionsFootnote}</p>
	{/if}
</div>

<style>
	.subscriptions {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.who {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.restaurant {
		font-weight: 600;
		color: var(--text-strong);
	}

	.owner {
		font-size: var(--text-2xs);
		color: var(--text-body);
		overflow-wrap: anywhere;
	}

	.mono {
		min-inline-size: 0;
		font-family: var(--font-mono);
	}

	.small {
		min-inline-size: 0;
		font-size: var(--text-sm);
	}

	.strong {
		font-weight: 600;
		color: var(--text-strong);
	}

	.state {
		min-inline-size: 0;
		display: flex;
	}

	.open {
		min-inline-size: 0;
		display: flex;
		justify-content: flex-end;
	}

	.note {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}
</style>
